import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "store/reducers";
import { AppDispatch } from "store/store";
import { setHasSetDefault } from "store/VideoSession/slice";
import { useGetVideoByPlaylistBroadcastId } from "hooks/useGetVideoByPlaylistBroadcastId";
import useCollectionWithVideos from "hooks/useCollectionWithVideos";
import { useSetNewCollectionVideo } from "hooks/useSetNewCollectionVideo";

export const useHandleDefaultState = () => {
    const dispatch = useDispatch<AppDispatch>();

    const { preselectedPlaylistBroadcastId, hasSetDefault } = useSelector(
        (s: RootState) => s.videoSession
    );

    const { collection, onDemandVideos, upcomingVideos, hasLoaded } =
        useCollectionWithVideos();

    const { getVideoFromPlaylistBroadcasts } =
        useGetVideoByPlaylistBroadcastId();

    const setNewCollectionVideo = useSetNewCollectionVideo();

    // handle default state
    useEffect(() => {
        if (
            upcomingVideos == null ||
            onDemandVideos == null ||
            !hasLoaded ||
            hasSetDefault
        )
            return;

        // Priority 1: Broadcast Preselected
        if (preselectedPlaylistBroadcastId) {
            const currentVideo = getVideoFromPlaylistBroadcasts(
                preselectedPlaylistBroadcastId
            );

            // If the video is found, force set it otherwise we will continue to the other defaults.
            if (currentVideo) {
                setNewCollectionVideo(currentVideo);

                // return immediately to prevent other handlings
                dispatch(setHasSetDefault(true));
                return;
            }
        }

        // Priority 2: Live Video
        if (
            upcomingVideos?.[0]?.broadcast?.details?.broadcastStatus ===
            "Active"
        ) {
            setNewCollectionVideo(upcomingVideos?.[0]);

            // return immediately to prevent other handlings
            dispatch(setHasSetDefault(true));
            return;
        }

        // Priority 3: Featured Trailer (auto-injected as first item in OnDemand)
        // Priority 4: First in Playlist
        setNewCollectionVideo(onDemandVideos?.[0]);

        // return immediately to prevent other handlings
        dispatch(setHasSetDefault(true));
    }, [
        dispatch,
        setNewCollectionVideo,
        hasSetDefault,
        upcomingVideos,
        collection,
        hasLoaded,
        onDemandVideos,
        preselectedPlaylistBroadcastId,
        getVideoFromPlaylistBroadcasts
    ]);
};
